import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  InputGroup,
  Form,
  Badge,
  Modal,
  Table,
  Spinner,
  Alert,
  Accordion,
} from "react-bootstrap";
import Sidebar from "../../components/Sidebar-left/sideBar";
import Header from "../../components/top-nav";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getOrganizations,
  getOrganizationDetails,
  assignOrganizationFramework,
  getFrameworksList
} from "../../service/common.service";
import { set } from "react-hook-form";

interface Framework {
  id: number;
  name: string;
  description: string;
  assigned_date: string;
  price: string;
  status: "PAID" | "PENDING";
}

interface Organization {
  id: number;
  slug: string;
  name: string;
  teamMembers: number;
  frameworks: Framework[];
  user_count?: number;
  framework_count?: number;
}

interface AvailableFramework {
  id: number;
  title: string;
  description: string;
  price: string;
}

interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

const FrameworkAssignment: React.FC = () => {
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const sidebarWidth = isSidebarHovered ? 240 : 80;

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const [availableFrameworks, setAvailableFrameworks] = useState<AvailableFramework[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [selectedFrameworks, setSelectedFrameworks] = useState<number[]>([]);
  const [loadingFrameworks, setLoadingFrameworks] = useState<{ [key: number]: boolean }>({});
  const [actionLoading, setActionLoading] = useState<{ [key: number]: boolean }>({});
  const [expandedOrg, setExpandedOrg] = useState<number | null>(null);

  const [organizationList, setOrganizationList] = useState<Organization[]>([]);
  const [selectedOrgModel, setselectedOrgModel] = useState<any>(null);
  const [assignedFrameworkIds, setAssignedFrameworkIds] = useState<number[]>([]);
  const [orgSearch, setOrgSearch] = useState("");
  const [orgResults, setOrgResults] = useState<Organization[]>([]);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  let searchTimeout: NodeJS.Timeout;

  const handleOrgSearch = (value: string) => {
    setOrgSearch(value);
    setShowOrgDropdown(true); // Add this line

    if (searchTimeout) clearTimeout(searchTimeout);

    if (value.trim() === "") {
      setOrgResults([]);
      setShowOrgDropdown(false); // Hide dropdown when search is empty
      return;
    }

    searchTimeout = setTimeout(() => {
      fetchOrganizationsForList(value);
    }, 400);
  };

  // Fetch organizations
  const fetchOrganizationsForList = async (search: string) => {
    try {
      const payload = {
        search,
        offset: 0, // Use 0 for search, not the main offset state
        limit: 10,
        counts: ["users", "frameworks"]
      };
      const response = await getOrganizations(payload);
      const apiData = response.data;
      if (apiData.status) {
        setOrgResults(apiData.data.data || apiData.data); // Handle both response formats
      } else {
        throw new Error("Failed to fetch organizations");
      }
    } catch (error) {
      console.error("Error fetching organizations for search:", error);
      setOrgResults([]);
    }
  };



  // Fetch organizations
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const payload = {
        search: debouncedSearchTerm, // Use debounced search term here
        offset,
        limit,
        counts: ["users", "frameworks"]
      };
      const response = await getOrganizations(payload);
      const apiData = response.data;
      console.log("org response", apiData);
      if (apiData.status) {
        setOrganizations(apiData.data.data);
        setTotal(apiData.data.pagination.total);
      } else {
        throw new Error("Failed to fetch organizations");
      }
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    // Don't call fetchOrganizations here - let the debounced effect handle it
  };

  // Fetch frameworks for a specific organization
  const fetchOrganizationFrameworks = async (orgId: number) => {
    try {
      setLoadingFrameworks(prev => ({ ...prev, [orgId]: true }));

      const response = await getOrganizationDetails(orgId);
      const apiData = response.data;

      if (apiData.status) {
        // Success case - map the response data to Framework format
        const orgFrameworks = apiData.data.frameworks.map((framework: any) => ({
          id: framework.framework_id, // Use framework_id as the main ID
          name: framework.framework_name,
          description: framework.framework_name, // You might want to get description from elsewhere
          assigned_date: framework.assigned_at,
          price: "", // Add price if available in your data
          status: "PENDING" as const, // Default to PENDING or determine based on your business logic
          // Additional fields from your response if needed
          assignment_id: framework.id,
          is_primary: framework.is_primary,
          assigned_by: framework.assigned_user_name,
          scope: framework.scope
        }));

        setOrganizations(prevOrgs =>
          prevOrgs.map(org =>
            org.id === orgId
              ? { ...org, frameworks: orgFrameworks }
              : org
          )
        );
      } else {
        throw new Error(apiData.message || "Failed to fetch organization frameworks");
      }

    } catch (error) {
      console.error("Error fetching organization frameworks:", error);
      setError(error instanceof Error ? error.message : "Failed to fetch frameworks");
    } finally {
      setLoadingFrameworks(prev => ({ ...prev, [orgId]: false }));
    }
  };

  // Assign frameworks to organization
  const assignFrameworks = async (orgId: number, frameworkIds: number[]) => {
    try {

      const payload = {
        framework_ids: frameworkIds
      };
      const response = await assignOrganizationFramework(orgId, payload);
      const apiData = response.data;
      if (apiData.status) {
        toast.success(apiData.message || "Frameworks assigned successfully");
        return true;
      } else {
        toast.error(apiData.message || "Failed to assign frameworks");
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to assign frameworks");
      return false;
    }
  };

  const fetchAvailableFrameworks = useCallback(async () => {
    try {
      const payload = {
        offset: 0,
        limit: 10
      };
      const response = await getFrameworksList(payload);
      const apiData = response.data;
      console.log("frameworks response", apiData);
      if (apiData.status) {
        setAvailableFrameworks(apiData.data);
      } else {
        throw new Error("Failed to fetch frameworks");
      }
    } catch (err) {
      setError("Failed to fetch available frameworks");
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
    fetchAvailableFrameworks();
  }, [debouncedSearchTerm, offset]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const toggleFrameworkSelection = (id: number) => {
    setSelectedFrameworks((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );
    console.log(id)
  };

  const handleSaveAssignment = async () => {
    if (!selectedOrgModel) return;
    const success = await assignFrameworks(selectedOrgModel.id, selectedFrameworks);
    if (success) {
      setSelectedFrameworks([]);
      setShowModal(false);
    }
  };



  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }


  const handleAccordionToggle = (orgId: number) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
    } else {
      setExpandedOrg(orgId);
      const org = organizations.find(o => o.id === orgId);
      // Load frameworks if not already loaded
      if (org && (!org.frameworks || org.frameworks.length === 0)) {
        fetchOrganizationFrameworks(orgId);
      }
    }
  };

  const handleAssignClick = (org: Organization) => {
    setSelectedOrg(org);
    setSelectedFrameworks([]);
    setExpandedOrg(null)
    setShowModal(true);
  };

  const handleAssignClickNew = () => {
    setSelectedFrameworks([]);
    setAssignedFrameworkIds([]);
    setselectedOrgModel(null);
    setExpandedOrg(null)
    setShowModal(true);
  };

  const handlePrev = () => {
    if (offset > 0) setOffset(offset - limit);
  };

  const handleNext = () => {
    if (offset + limit < total) setOffset(offset + limit);
  };


  const handleOrgSelect = async (orgId: number) => {
    if (!orgId) return;

    // Find the selected organization from orgResults instead of organizations
    const selected = orgResults.find((o) => o.id === Number(orgId));
    setselectedOrgModel(selected);
    setOrgSearch(selected?.name || ""); // Set the search input to the selected org name
    setShowOrgDropdown(false); // Close dropdown after selection

    // Fetch assigned frameworks API
    const response = await getOrganizationDetails(orgId);
    const apiData = response.data;
    if (apiData.status) {
      const assignedIds = apiData.data.frameworks.map((fw: any) => fw.framework_id);
      setAssignedFrameworkIds(assignedIds);
      // Remove previously selected if they are assigned already
      setSelectedFrameworks((prev) =>
        prev.filter((id) => !assignedIds.includes(id))
      );
    }
  };


  console.log("organizations", organizations);
  return (
    <div>
      {/* Sidebar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "100%",
          width: `${sidebarWidth}px`,
          transition: "width 0.3s",
          zIndex: 1000,
        }}
        onMouseEnter={() => setIsSidebarHovered(true)}
        onMouseLeave={() => setIsSidebarHovered(false)}
      >
        <Sidebar isCollapsed={!isSidebarHovered} />
      </div>

      {/* Main Content */}
      <div
        className="main-content"
        style={{
          marginLeft: `${sidebarWidth}px`,
          transition: "margin-left 0.3s",
        }}
      >
        <Header />
        <Container fluid className="px-4">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="fw-bold mb-0">Framework Assignment</h4>
              <p className="text-muted mb-0">
                Assign compliance frameworks to organizations
              </p>
            </div>
            <div className="d-flex align-items-center">
              <InputGroup className="me-2" style={{ width: "250px" }}>
                <Form.Control
                  placeholder="Search organizations..."
                  value={searchTerm}
                  onChange={handleSearchChange} // Use the new handler
                />
              </InputGroup>
              <Button variant="primary" onClick={() => handleAssignClickNew()}>
                <i className="bi bi-plus-lg me-1"></i> Assign Framework
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          {/* Organizations as Separate Cards with Accordion */}
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2">Loading organizations...</p>
            </div>
          ) : organizations?.length === 0 ? (
            <div className="text-center text-muted py-4">
              {searchTerm ? "No organizations match your search criteria." : "No organizations found."}
            </div>
          ) : (
            <>
              {/* Organization Cards */}
              {organizations.map((org) => (
                <Card key={org.id} className="shadow-sm border-0 rounded-3 mb-4">
                  <Card.Body>
                    {/* Organization Header */}
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1">
                        <h6 className="fw-bold mb-1">
                          <i className="bi bi-building me-2 text-primary"></i>
                          {org.name}
                        </h6>
                        <small className="text-muted">
                          {org.framework_count} frameworks assigned | {org.user_count} team members
                        </small>
                      </div>
                      <div className="d-flex gap-2">
                        {/* <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleAssignClick(org)}
                          disabled={actionLoading[org.id]}
                        >
                          {actionLoading[org.id] ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-1"
                              />
                              Assigning...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-plus-lg me-1"></i> Assign Framework
                            </>
                          )}
                        </Button> */}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleAccordionToggle(org.id)}
                        >
                          {expandedOrg === org.id ? (
                            <>
                              <i className="bi bi-chevron-up me-1"></i> Hide Frameworks
                            </>
                          ) : (
                            <>
                              <i className="bi bi-chevron-down me-1"></i> View Frameworks
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Accordion Content - Frameworks */}
                    {expandedOrg === org.id && (
                      <div className="mt-4 pt-3 border-top">
                        {loadingFrameworks[org.id] ? (
                          <div className="text-center py-3">
                            <Spinner animation="border" size="sm" variant="primary" />
                            <span className="ms-2 text-muted">Loading frameworks...</span>
                          </div>
                        ) : org.frameworks && org.frameworks.length > 0 ? (
                          <Row>
                            {org.frameworks.map((framework) => (
                              <Col md={6} lg={4} key={framework.id} className="mb-3">
                                <Card className="border rounded-3 h-100">
                                  <Card.Body className="d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <div className="d-flex align-items-center">
                                        <i className="bi bi-shield-check text-primary me-2"></i>
                                        <div>
                                          <strong className="d-block">{framework.name}</strong>
                                          <p className="text-muted small mb-0">
                                            {framework.description}
                                          </p>
                                        </div>
                                      </div>
                                      <Badge
                                        bg={framework.status === "PAID" ? "success" : "warning"}
                                        className="px-3 py-2"
                                      >
                                        {framework.status === "PAID" ? "PAID" : "PENDING"}
                                      </Badge>
                                    </div>

                                    <div className="mt-auto">
                                      <small className="text-muted d-block">
                                        <i className="bi bi-calendar me-1"></i>
                                        Assigned {framework.assigned_date}
                                      </small>
                                      <strong className="text-primary">{framework.price}</strong>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}

                          </Row>
                        ) : (
                          <div className="text-center text-muted py-4">
                            <i className="bi bi-inbox display-4 d-block mb-2"></i>
                            No frameworks assigned yet
                            <div className="mt-2">
                              {/* <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleAssignClick(org)}
                              >
                                <i className="bi bi-plus-lg me-1"></i> Assign First Framework
                              </Button> */}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}

              {/* Pagination */}
              {organizations.length > 0 && (
                // <Card className="shadow-sm border-0 rounded-3">
                //   <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">
                        Showing {offset + 1}–{Math.min(offset + limit, total)} of {total} organizations
                        {searchTerm && " (filtered)"}
                      </span>
                      <div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2"
                          onClick={handlePrev}
                          disabled={offset === 0}
                        >
                          <i className="bi bi-chevron-left"></i> Prev
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleNext}
                          disabled={offset + limit >= total}
                        >
                          Next <i className="bi bi-chevron-right"></i>
                        </Button>
                      </div>
                    </div>
                //   </Card.Body>
                // </Card>
              )}
            </>
          )}
        </Container>

        {/* Assign Framework Modal */}

        <Modal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setShowOrgDropdown(false); // Close dropdown when modal closes
          }}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>Assign Framework</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {/* Organization Select */}
            <Form.Group className="mb-4 position-relative">
              <Form.Label className="fw-semibold">Select Organization</Form.Label>

              <Form.Control
                type="text"
                placeholder="Search organization..."
                value={orgSearch}
                onChange={(e) => handleOrgSearch(e.target.value)}
                autoComplete="off"
              />

              {/* Dropdown List */}
              {showOrgDropdown && orgResults.length > 0 && (
                <div
                  className="border rounded mt-1 bg-white position-absolute w-100 shadow-sm"
                  style={{ zIndex: 9999, maxHeight: "200px", overflowY: "auto" }}
                >
                  {orgResults.map((org) => (
                    <div
                      key={org.id}
                      className="p-2 org-item hover-bg-light"
                      onClick={() => handleOrgSelect(org.id)}
                      style={{
                        cursor: "pointer",
                        borderBottom: "1px solid #f0f0f0"
                      }}
                    >
                      <div className="fw-medium">{org.name}</div>
                      <small className="text-muted">
                        {org.user_count} members • {org.framework_count} frameworks
                      </small>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>

            {/* Framework List */}
            <Row xs={1} md={2} className="g-3 align-items-stretch">
              {availableFrameworks.map((fw) => {
                const isSelected = selectedFrameworks.includes(fw.id);
                const isAlreadyAssigned = assignedFrameworkIds.includes(fw.id); // DISABLED

                return (
                  <Col key={fw.id}>
                    <Card
                      className={`h-100 border rounded-3 
                ${isSelected ? "border-success bg-light" : ""}
                ${isAlreadyAssigned ? "opacity-50" : ""}`}
                    >
                      <Card.Body className="d-flex flex-column">
                        <div className="mb-1">
                          <strong className="d-block">{fw.title}</strong>
                          <p className="text-muted small mb-0 line-clamp-2">
                            {fw.description}
                          </p>
                          <small className="text-primary fw-bold">{fw.price}</small>

                          {isAlreadyAssigned && (
                            <div className="text-success small fw-bold">
                              Already assigned
                            </div>
                          )}
                        </div>

                        <Button
                          variant={isSelected ? "outline-success" : "outline-primary"}
                          className="mt-auto w-100"
                          disabled={isAlreadyAssigned}
                          onClick={() => toggleFrameworkSelection(fw.id)}
                        >
                          {isAlreadyAssigned
                            ? "Assigned"
                            : isSelected
                              ? "Selected"
                              : "+ Assign Framework"}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Modal.Body>

          <Modal.Footer className="align-center">
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              variant="success"
              disabled={!selectedOrgModel || selectedFrameworks.length === 0}
              onClick={handleSaveAssignment}
            >
              Save Assignment
            </Button>
          </Modal.Footer>
        </Modal>
        <ToastContainer position="top-right" autoClose={2500} />

      </div >
    </div >
  );
};

export default FrameworkAssignment;